<script lang="ts">
	import classNames from 'classnames';
	import { onMount } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { sortCharts } from '../../utils/sortCharts/sortChart';
	import { NewNewsCard } from '../NewNewsCard';
	import { NewsDataChart } from '../NewsDataChart';
	import { NewsletterWidget } from '../NewsDataChart/NewsletterWidget';
	import { NewsJhaAdvertCard } from './NewsJhaAdvertCard';
	import { SiteNames } from '#lib/constants/site-names.js';
	import { NewsMixpanelEvents } from '#lib/features/news/constants/mixpanel-events.js';
	import { filterArticlesForColumn } from '#lib/features/news/utils/filterArticlesForColumn/filterArticlesForColumn.js';
	import { page } from '$app/state';
	import { Container } from '$global/components/Container';
	import { track } from '$global/utils/track';
	import type { NewsHomePageData } from '$routes/news/types';

	let {
		articles = [],
		loading,
		classes = {},
		onClick = undefined,
		widgetMode = 'classic',
		showCharts = true,
		showProfileCharts = false,
	}: Props = $props();

	interface Props {
		widgetMode?: 'classic' | 'column';
		class?: string;
		articles?: Array<{
			id: string;
			title?: string | null;
			description?: string | null;
			image?: string;
			categories?: string[];
			geotag?: { name: string } | null;
			artists?: Array<
				{ HENI_News_Artists_id?: { name?: string } | null } | null | undefined
			>;
			url?: string | null;
			attribution?: string | null;
			date_created?: string | null;
			date_updated?: string | null;
			publishDate?: string | null;
		}>;
		loading: boolean;
		classes?: {
			container?: string;
			columnContainer?: string;
			card?: string;
			cardContainer?: string;
		};
		onClick?: (() => void) | undefined;
		showCharts?: boolean;
		showProfileCharts?: boolean;
	}

	const handleClickCard = (id: string, position: number) => {
		track(NewsMixpanelEvents.ClickArticle, {
			id,
			position,
		});
	};

	let showGlobalNav = $derived(page.data.showGlobalNav ?? true);

	let charts = $derived(
		(() => {
			if (showProfileCharts) {
				return page.data.profileCharts as NewsHomePageData['profileCharts'];
			}

			return showCharts
				? sortCharts(
						(page.data.charts as NewsHomePageData['charts']) || [],
						page.url.searchParams.get('category') !== 'exclusives',
						1,
						true
					)
				: [];
		})()
	);

	// Every grid below keys its `#each` on `item.id`, and Svelte throws
	// `each_key_duplicate` on a repeat rather than rendering — so one repeated
	// article takes down the whole feed. Offset pagination hands us repeats
	// legitimately: an article published between two page requests shifts the
	// window and re-serves the item on its boundary. First occurrence wins, so
	// the feed keeps its page order.
	let uniqueArticles = $derived(
		(() => {
			const seen = new Set<string>();

			return (articles ?? []).filter((article) => {
				if (seen.has(article.id)) {
					return false;
				}

				seen.add(article.id);

				return true;
			});
		})()
	);

	let articlesOrNull = $derived(
		loading ? Array(12).fill(null) : uniqueArticles
	);
	let secondColumn: null | HTMLDivElement = $state(null);
	let thirdColumn: null | HTMLDivElement = $state(null);
	let columnContainer: null | HTMLDivElement = $state(null);

	// The widget column is position:fixed, so it is scrolled by hand via
	// marginTop: it moves with the page until the last widget rests with the
	// same gap below it as the page margin to its right (a travel of
	// `bottomLimit`), then sticks, and once the grid end scrolls into view its
	// bottom tracks the grid bottom so it never overlaps the footer. When the
	// column is taller than the grid (short feed with the load-more button),
	// that travel cannot fit in the scroll distance to the grid end, so the
	// column scrolls proportionally faster than the page — keeping its start
	// position at the top while still meeting the grid end without a jump.
	// Everything is measured from the DOM rather than from header-height
	// constants: the header stack above the grid varies (saved-tags bar,
	// global nav), and a hardcoded height parks the column early, leaving an
	// oversized bottom gap.
	const setColumnScroll = (column: HTMLDivElement | null) => {
		const lastCard = column?.lastElementChild;

		if (!column || !lastCard || !columnContainer) {
			return;
		}

		const currentShift = parseFloat(column.style.marginTop) || 0;
		const lastCardRect = lastCard.getBoundingClientRect();
		const rightGap = window.innerWidth - lastCardRect.right;

		const bottomLimit = Math.max(
			0,
			lastCardRect.bottom - currentShift - (window.innerHeight - rightGap)
		);

		const gridBottom = columnContainer.getBoundingClientRect().bottom;
		// Scroll position at which the grid bottom meets the viewport bottom
		const gridEndScrollY = gridBottom + window.scrollY - window.innerHeight;

		const speed =
			bottomLimit > gridEndScrollY && gridEndScrollY > 0
				? bottomLimit / gridEndScrollY
				: 1;

		const afterGridDifference = Math.max(0, window.innerHeight - gridBottom);

		column.style.marginTop = `${-Math.min(
			window.scrollY * speed,
			bottomLimit + afterGridDifference
		)}px`;
	};

	$effect(() => {
		if (articlesOrNull[0] && widgetMode === 'column') {
			setColumnScroll(thirdColumn);
			setColumnScroll(secondColumn);
		}
	});

	onMount(() => {
		if (widgetMode === 'column') {
			const listener = () => {
				if (articlesOrNull[0]) {
					setColumnScroll(secondColumn);
					setColumnScroll(thirdColumn);
				}
			};

			window.addEventListener('scroll', listener);
			window.addEventListener('resize', listener);

			return () => {
				window.removeEventListener('scroll', listener);
				window.removeEventListener('resize', listener);
			};
		}
	});
</script>

<Container
	dataCy="news-grid"
	class={twMerge('px-0 pt-0 sm:pt-8 lg:pt-8', classes.container)}
>
	{#if articlesOrNull}
		<div
			class={twMerge('-mx-2 flex flex-wrap', classes.columnContainer)}
			bind:this={columnContainer}
		>
			<!-- xs: render all articlesOrNull -->
			{#each articlesOrNull as item, i (item?.id || `${page.url.searchParams.get('category')}-${i}`)}
				<div class={twMerge('w-full px-2 sm:hidden', classes?.cardContainer)}>
					{#if widgetMode === 'column'}
						{#if i === 1}
							<!-- On phones this widget is the largest element above the fold. -->
							<NewsletterWidget
								priority
								classes={{
									background: 'object-cover',
								}}
							/>
						{/if}
						{#if showGlobalNav && !loading && i === 3}
							<NewsJhaAdvertCard
								feedIndexNumber={i}
								classes={{
									container: twMerge('', classes?.cardContainer),
								}}
							/>
						{/if}
						{#if i % 4 === 1}
							<NewsDataChart
								{showGlobalNav}
								class="border-x-0"
								chart={charts?.[
									Math.floor(i / 4) + (showProfileCharts ? 0 : 1)
								]}
								classes={{
									newsletterWidget: 'hidden',
								}}
							/>
						{/if}
						<NewNewsCard
							{item}
							width={512}
							priority={i === 0}
							clampable
							classes={{
								card: classNames(classes.card, {
									'mt-[0px]': !i && item?.attribution === SiteNames.HeniNews,
								}),
							}}
							{onClick}
							onclick={() => {
								handleClickCard(item.id, i);
							}}
						/>
					{:else}
						<NewNewsCard
							width={512}
							priority={i === 0}
							{item}
							clampable
							classes={{
								card: classNames(classes.card),
							}}
							{onClick}
							onclick={() => {
								handleClickCard(item.id, i);
							}}
						/>
					{/if}
				</div>
			{/each}

			<!-- sm: render 2 columns -->
			{#each [0, 1] as col, columnIndex}
				{#if (widgetMode === 'column' ? [0] : [0, 1]).includes(columnIndex)}
					<div class="mb-4 hidden px-3 sm:block sm:w-1/2 lg:hidden">
						{#each widgetMode === 'column' ? articlesOrNull : filterArticlesForColumn(articlesOrNull, col, 'md') as item, i (item?.id ? `sm-${item?.id}` : `sm-${page.url.searchParams.get('category')}-${i}`)}
							{#if showGlobalNav && ((!loading && widgetMode === 'column' && i === 3) || (widgetMode === 'classic' && i === 3 && columnIndex === 0))}
								<NewsJhaAdvertCard
									feedIndexNumber={i}
									classes={{
										container: twMerge('mb-6', classes?.cardContainer),
									}}
								/>
							{/if}
							{#if showProfileCharts && showGlobalNav && i % 3 === (columnIndex === 1 ? 0 : 2)}
								<NewsDataChart
									{showGlobalNav}
									class="mb-6"
									chart={charts?.[
										columnIndex === 1 ? (i / 3) * 2 : ((i - 2) / 3) * 2 + 1
									]}
									classes={{
										newsletterWidget: 'hidden',
									}}
								/>
							{/if}
							<div class={twMerge('mb-6', classes?.cardContainer)}>
								<NewNewsCard
									{item}
									classes={{
										card: classes.card,
									}}
									{onClick}
									onclick={() => {
										if (item.id) {
											handleClickCard(item.id, 2 * i + columnIndex);
										}
									}}
								/>
							</div>
						{/each}
					</div>
				{:else if !articlesOrNull[0] && widgetMode === 'column'}
					<div class="mb-4 hidden px-3 sm:block sm:w-1/2 lg:hidden">
						{#each articlesOrNull.slice(0, articlesOrNull.length / 2)}
							<div class={twMerge('mb-6', classes?.cardContainer)}>
								<NewNewsCard
									item={null}
									classes={{
										card: classes.card,
									}}
								/>
							</div>
						{/each}
					</div>
				{:else if widgetMode === 'column'}
					<div class="relative">
						<div
							class="fixed mb-4 hidden px-3 sm:block sm:w-[calc((min(100vw-48px,1232px))/2)] lg:hidden"
							bind:this={secondColumn}
						>
							{#if charts}
								{#each charts as chart, i}
									<NewsDataChart
										{showGlobalNav}
										classes={{
											top: 'sm:h-auto',
											newsletterWidget: 'mb-6',
											newsletterWidgetClasses: {
												background: 'object-cover',
											},
										}}
										class={classNames(
											'sm:h-auto',
											i < charts.length - 1 ? 'mb-6' : 'mb-4'
										)}
										{chart}
									/>
								{/each}
							{/if}
						</div>
					</div>
				{/if}
			{/each}

			<!-- lg: render 3 columns -->
			{#each [0, 1, 2] as col, columnIndex}
				{#if (widgetMode === 'column' ? [0, 1] : [0, 1, 2]).includes(columnIndex)}
					<div
						class={classNames('mb-4 hidden px-2 lg:block lg:w-1/3', {
							'pr-3': columnIndex === 0,
							'pr-4 pl-3': columnIndex === 1,
						})}
					>
						{#each filterArticlesForColumn(articlesOrNull, col, widgetMode === 'column' ? 'md' : 'lg') as item, i (item?.id ? `md-${item?.id}` : `md-${page.url.searchParams.get('category')}-${i}`)}
							{#if showGlobalNav && !loading && columnIndex === 1 && i === 1}
								<NewsJhaAdvertCard
									feedIndexNumber={i}
									classes={{
										container: twMerge('mb-6', classes?.cardContainer),
									}}
								/>
							{/if}
							{#if showProfileCharts && showGlobalNav && i % 3 === 2 - columnIndex}
								<NewsDataChart
									{showGlobalNav}
									class="mb-6"
									chart={charts?.[i]}
									classes={{
										newsletterWidget: 'hidden',
									}}
								/>
							{/if}
							<div class={twMerge('mb-6', classes?.cardContainer)}>
								<NewNewsCard
									{item}
									classes={{
										card: classes.card,
									}}
									{onClick}
									onclick={() => {
										if (item.id) {
											const position =
												(widgetMode === 'column' ? 2 : 3) * i + columnIndex;
											handleClickCard(item.id, position);
										}
									}}
								/>
							</div>
						{/each}
					</div>
				{:else if !articlesOrNull[0] && widgetMode === 'column'}
					<div class="mb-4 hidden pr-1 pl-4 lg:block lg:w-1/3">
						{#each articlesOrNull.slice(0, articlesOrNull.length / 3)}
							<div class={twMerge('mb-4', classes?.cardContainer)}>
								<NewNewsCard
									item={null}
									classes={{
										card: classes.card,
									}}
								/>
							</div>
						{/each}
					</div>
				{:else if widgetMode === 'column'}
					<div class="relative">
						<div
							class="fixed mb-4 hidden pr-1 pl-4 lg:block lg:w-[calc((min(100vw-48px,1232px))/3)]"
							bind:this={thirdColumn}
						>
							{#if charts}
								{#each charts as chart}
									<NewsDataChart
										{showGlobalNav}
										class={classNames('mb-4 sm:h-auto')}
										classes={{
											top: 'sm:h-auto',
											newsletterWidget: 'mb-4',
											newsletterWidgetClasses: {
												background: 'object-cover',
											},
										}}
										{chart}
									/>
								{/each}
							{/if}
						</div>
					</div>
				{/if}
			{/each}
		</div>
	{/if}
</Container>
